import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { LoadErrorBox } from "../../components/common/LoadErrorBox";
import { Card } from "../../components/ui/card";
import { DateInputIT } from "../../components/common/DateTimeIT";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "../../components/ui/table";
import { StatusBadge } from "../../components/StatusBadge";
import { Download } from "lucide-react";
import { API } from "../../lib/api";

export default function AdminLogs() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    user_id: "all", service_id: "all", status: "all", cache_hit: "all", date_from: "", date_to: "",
  });

  const [failed, setFailed] = useState(false);
  const load = async () => {
    setLoading(true);
    const params = {};
    if (filters.user_id !== "all") params.user_id = filters.user_id;
    if (filters.service_id !== "all") params.service_id = filters.service_id;
    if (filters.status !== "all") params.status = filters.status;
    if (filters.cache_hit !== "all") params.cache_hit = filters.cache_hit === "yes";
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    try {
      const { data } = await api.get("/admin/logs", { params });
      setRows(data);
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/admin/users").then(({ data }) => setUsers(data));
    api.get("/admin/services").then(({ data }) => setServices(data));
  }, []);
  useEffect(() => { load(); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const onExport = async () => {
    const token = localStorage.getItem("wattline_token");
    const params = new URLSearchParams();
    if (filters.user_id !== "all") params.append("user_id", filters.user_id);
    if (filters.service_id !== "all") params.append("service_id", filters.service_id);
    if (filters.status !== "all") params.append("status", filters.status);
    if (filters.cache_hit !== "all") params.append("cache_hit", filters.cache_hit === "yes");
    if (filters.date_from) params.append("date_from", filters.date_from);
    if (filters.date_to) params.append("date_to", filters.date_to);
    const res = await fetch(`${API}/admin/logs/export?${params}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `wattline_logs_${new Date().toISOString().slice(0, 10)}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl tracking-tight font-medium text-text-primary">Log attività</h1>
          <p className="text-sm text-text-secondary mt-1">Tutte le richieste eseguite sulla piattaforma.</p>
        </div>
        <Button onClick={onExport} data-testid="export-logs-button"
          className="rounded-lg bg-brand hover:bg-brand-hover text-white h-10 px-4 font-medium">
          <Download size={16} className="mr-1.5" /> Esporta Excel
        </Button>
      </div>

      <Card className="bg-surface border border-border rounded-xl shadow-sm p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-text-secondary">Utente</Label>
            <Select value={filters.user_id} onValueChange={(v) => setFilters({ ...filters, user_id: v })}>
              <SelectTrigger data-testid="logs-filter-user" className="h-10 rounded-lg border-border bg-white"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.email}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-text-secondary">Servizio</Label>
            <Select value={filters.service_id} onValueChange={(v) => setFilters({ ...filters, service_id: v })}>
              <SelectTrigger data-testid="logs-filter-service" className="h-10 rounded-lg border-border bg-white"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti</SelectItem>{services.map((s) => <SelectItem key={s.service_id} value={s.service_id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-text-secondary">Stato</Label>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger data-testid="logs-filter-status" className="h-10 rounded-lg border-border bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="completed">Completata</SelectItem>
                <SelectItem value="failed">Fallita</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-text-secondary">Cache</Label>
            <Select value={filters.cache_hit} onValueChange={(v) => setFilters({ ...filters, cache_hit: v })}>
              <SelectTrigger data-testid="logs-filter-cache" className="h-10 rounded-lg border-border bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="yes">Solo cache</SelectItem>
                <SelectItem value="no">Solo live</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-text-secondary">Dal</Label>
            <DateInputIT testId="logs-filter-date-from"
              value={filters.date_from} onChange={(v) => setFilters({ ...filters, date_from: v })}
              className="h-10 rounded-lg" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-text-secondary">Al</Label>
            <DateInputIT testId="logs-filter-date-to" min={filters.date_from}
              value={filters.date_to} onChange={(v) => setFilters({ ...filters, date_to: v })}
              className="h-10 rounded-lg" />
          </div>
        </div>
      </Card>

      <Card className="bg-surface border border-border rounded-xl shadow-sm overflow-hidden">
        <Table data-testid="logs-table">
          <TableHeader>
            <TableRow className="bg-subtle hover:bg-subtle">
              <TableHead>Data</TableHead>
              <TableHead>Utente</TableHead>
              <TableHead>Servizio</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead>Cache</TableHead>
              <TableHead className="text-right">Costo (€)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center text-text-muted py-8 text-sm">Caricamento…</TableCell></TableRow>
            ) : failed ? (
              <TableRow><TableCell colSpan={6}><LoadErrorBox what="i log" onRetry={load} testId="logs-load-error" /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-text-muted py-12 text-sm">Nessun log trovato</TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.id} data-testid={`log-row-${r.id}`}>
                <TableCell className="text-sm text-text-secondary whitespace-nowrap">{new Date(r.created_at).toLocaleString("it-IT")}</TableCell>
                <TableCell className="text-sm text-text-primary">{r.user_email}</TableCell>
                <TableCell className="text-sm text-text-primary">{r.service_name}</TableCell>
                <TableCell><StatusBadge status={r.status} cacheHit={r.cache_hit} /></TableCell>
                <TableCell className="text-sm text-text-secondary">{r.cache_hit ? "Sì" : "No"}</TableCell>
                <TableCell className="text-right text-sm font-medium text-text-primary">{((r.cost_cents || 0) / 100).toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
